import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl, money, apiErr } from "@/lib/api";
import { Button, Badge, Empty, SortControl, sortRows } from "@/lib/ui";

function Timer({ hours }) {
  const tone = hours >= 48 ? "red" : hours >= 24 ? "amber" : "green";
  return <Badge tone={tone} className="mono" ><i className="fa-solid fa-clock" /> {hours}h pending</Badge>;
}

export default function PendingProducts() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("all");
  const [sort, setSort] = useState({ field: "product", dir: "asc" });
  const SORT_FIELDS = [
    { key: "hours_pending", label: "Time Pending", type: "num" },
    { key: "submitted_at", label: "Submitted", type: "date" },
    { key: "product", label: "Product", type: "str" },
    { key: "discount", label: "Discount", type: "num" },
  ];

  const load = () => api.get("/admin/pending-products").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Reason for rejection?") || "";
        await api.post(`/admin/products/${id}/reject`, { reason });
      } else {
        await api.post(`/admin/products/${id}/approve`);
      }
      toast.success(`Product ${action}d`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <p className="text-slate-400">Loading…</p>;
  const buckets = data.buckets;
  const map = { all: data.products, newly: buckets.newly, lt24: buckets.lt24, "24to48": buckets["24to48"], over48: buckets.over48 };
  const list = map[view] || [];
  const tabs = [
    { k: "all", t: `All (${data.products.length})` },
    { k: "newly", t: `Newly (${buckets.newly.length})` },
    { k: "lt24", t: `< 24h (${buckets.lt24.length})` },
    { k: "24to48", t: `24–48h (${buckets["24to48"].length})` },
    { k: "over48", t: `Over 48h (${buckets.over48.length})` },
  ];

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Pending Products</h1>
      <p className="mt-1 text-sm text-slate-500">Approval target within 48 hours from submission.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`pending-tab-${t.k}`} onClick={() => setView(t.k)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${view === t.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{t.t}</button>
        ))}
      </div>

      <div className="mt-3"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="pending-sort" /></div>

      {list.length === 0 ? <div className="mt-6"><Empty text="No products in this bucket" icon="fa-clipboard-check" /></div> : (
        <div className="mt-4 space-y-3">
          {sortRows(list, SORT_FIELDS, sort).map((p) => (
            <div key={p.id} data-testid={`pending-${p.id}`} className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
              <img src={mediaUrl(p.catalogue_image)} alt={p.product} className="h-24 w-32 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-head font-bold text-slate-900">{p.product}</p>
                  <Timer hours={p.hours_pending} />
                  {p.manufacturer_stock && <Badge tone="blue">Mfg</Badge>}
                  {p.pan_india && <Badge tone="orange">Pan India</Badge>}
                </div>
                <p className="text-sm text-slate-500">{p.category} · {p.size} · {p.company_name} ({p.seller_type}) · {p.city}</p>
                <p className="text-sm font-semibold text-slate-700">{money(p.primary_rate)}/{p.primary_unit} <span className="strike text-slate-400">{money(p.actual_rate)}</span> · {p.discount}% OFF</p>
                <p className="mono text-xs text-slate-400">Submitted {new Date(p.submitted_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="green" onClick={() => act(p.id, "approve")} data-testid={`approve-${p.id}`}><i className="fa-solid fa-check" /> Approve</Button>
                <Button variant="danger" onClick={() => act(p.id, "reject")} data-testid={`reject-${p.id}`}><i className="fa-solid fa-xmark" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
